import React, { FC, useMemo } from "react";
import { MatterTimeline, Phase, PricingLevel } from "@design-system";
import { useMutation } from "../../hooks";
import { UPDATE_PHASE, UPDATE_TASK, UPDATE_PHASES, UPDATE_TASKS } from "../../graphql/mutation";

import styles from "./timeline-tab-container.css";
import { PHASE_FRAGMENT } from "~/graphql/fragment/phase-fragment";
import { TASK_FRAGMENT } from "~/graphql/fragment/task-fragment";

const { ["container"]: containerClassName, ["body-section"]: bodySection } = styles as any;

type Props = {
  matterStartDate: Date | null;
  matterDeadlineDate: Date | null;
  matterId: number;
  pricingLevel: PricingLevel;
  phases: Phase[];
  onUpdateMatterDates: (startDate: Date | null, deadlineDate: Date | null) => void;
  currencySymbol: string;
  timelineActivationKey: number;
};

export const TimelineTabContainer: FC<Props> = ({
  matterId,
  phases,
  matterStartDate,
  matterDeadlineDate,
  pricingLevel,
  onUpdateMatterDates,
  currencySymbol,
  timelineActivationKey,
}) => {
  const [updatePhase] = useMutation(UPDATE_PHASE);
  const [updateTask] = useMutation(UPDATE_TASK);
  const [updatePhases] = useMutation(UPDATE_PHASES);
  const [updateTasks] = useMutation(UPDATE_TASKS);

  const memoizedPhases = useMemo(() => phases, [phases]);

  return (
    <div className={containerClassName}>
      <div className={bodySection}>
        <MatterTimeline
          matterStartDate={matterStartDate}
          matterDeadlineDate={matterDeadlineDate}
          pricingLevel={pricingLevel}
          phases={memoizedPhases}
          currencySymbol={currencySymbol}
          matterId={matterId}
          timelineActivationKey={timelineActivationKey}
          onUpdateMatterDates={onUpdateMatterDates}
          onUpdatePhase={async (phase) => {
            await updatePhase({
              variables: {
                phaseId: phase.id,
                matterId,
                data: {
                  ...(phase.startDate !== undefined && { startDate: { set: phase.startDate } }),
                  ...(phase.deadlineDate !== undefined && {
                    deadlineDate: { set: phase.deadlineDate },
                  }),
                },
              },
              update: (cache, newItem) => {
                if (!newItem.data?.updatePhase) return;
                const newPhase = newItem.data.updatePhase;
                cache.writeFragment({
                  id: `Phase:${newPhase.id}`,
                  data: newPhase,
                  fragment: PHASE_FRAGMENT,
                  fragmentName: "PhaseParts",
                });
              },
            });
          }}
          onUpdateTask={(task, phaseId) =>
            updateTask({
              variables: {
                taskId: task.id!,
                data: {
                  ...(task.startDate !== undefined && { startDate: { set: task.startDate } }),
                  ...(task.deadlineDate !== undefined && {
                    deadlineDate: { set: task.deadlineDate },
                  }),
                },
              },
              update: (cache, newItem) => {
                if (!newItem.data?.updateTask) return;
                const newTask = newItem.data.updateTask;
                cache.modify({
                  id: `Phase:${phaseId}`,
                  fields: {
                    tasks(existingTaskRefs = [], { toReference }) {
                      const newTaskRef = toReference({ __typename: "Task", id: newTask.id });
                      if (
                        existingTaskRefs.some(
                          (ref: { __ref: string }) => ref.__ref === newTaskRef?.__ref
                        )
                      ) {
                        return existingTaskRefs;
                      }
                      return [...existingTaskRefs, newTaskRef];
                    },
                  },
                });
              },
            })
          }
          onUpdatePhases={async (phases) => {
            await updatePhases({
              variables: {
                matterId,
                data: phases.map((phase) => ({
                  id: phase.id,
                  payload: {
                    ...(phase.startDate !== undefined && { startDate: { set: phase.startDate } }),
                    ...(phase.deadlineDate !== undefined && {
                      deadlineDate: { set: phase.deadlineDate },
                    }),
                  },
                })),
              },
              update: (cache, newItem) => {
                if (!newItem.data?.updatePhases) return;
                const updatedPhases = newItem.data.updatePhases;
                updatedPhases.forEach((phase) => {
                  cache.writeFragment({
                    id: `Phase:${phase.id}`,
                    data: phase,
                    fragment: PHASE_FRAGMENT,
                    fragmentName: "PhaseParts",
                  });
                });
              },
            });
          }}
          onUpdateTasks={async (tasks) => {
            await updateTasks({
              variables: {
                data: tasks.map((task) => ({
                  id: task.id!,
                  data: {
                    ...(task.startDate !== undefined && { startDate: { set: task.startDate } }),
                    ...(task.deadlineDate !== undefined && {
                      deadlineDate: { set: task.deadlineDate },
                    }),
                  },
                })),
              },
              update: (cache, newItem) => {
                if (!newItem.data?.updateTasks) return;
                const updatedTasks = newItem.data.updateTasks;

                const tasksByPhase = new Map<number, any[]>();

                updatedTasks.forEach((task) => {
                  const taskId = task.id;
                  const taskRef = cache.identify({ __typename: "Task", id: taskId });
                  if (!taskRef) return;

                  const taskData = cache.readFragment({
                    id: taskRef,
                    fragment: TASK_FRAGMENT,
                    fragmentName: "TaskParts",
                  });

                  if (!taskData || !taskData.phaseId) return;
                  const phaseId = taskData.phaseId;

                  if (!tasksByPhase.has(phaseId)) {
                    tasksByPhase.set(phaseId, []);
                  }
                  tasksByPhase.get(phaseId)!.push(task);
                });

                tasksByPhase.forEach((phaseTasks, phaseId) => {
                  cache.modify({
                    id: `Phase:${phaseId}`,
                    fields: {
                      tasks(existingTaskRefs = [], { toReference }) {
                        const existingTaskMap = new Map(
                          existingTaskRefs.map((ref: { __ref: string }) => [ref.__ref, ref])
                        );

                        phaseTasks.forEach((task) => {
                          const newTaskRef = toReference({ __typename: "Task", id: task.id });
                          if (newTaskRef && !existingTaskMap.has(newTaskRef.__ref)) {
                            existingTaskMap.set(newTaskRef.__ref, newTaskRef);
                          }
                        });

                        return Array.from(existingTaskMap.values());
                      },
                    },
                  });
                });
              },
            });
          }}
        />
      </div>
    </div>
  );
};
